import { useEffect, useRef, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Spin, Button, Input } from 'antd';
import {
    EnvironmentOutlined,
    LoadingOutlined,
    CloseOutlined,
    SearchOutlined,
    AimOutlined,
} from '@ant-design/icons';
import { fetchPickupPoints, selectPickupPoint } from '../../features/shipping';
import { shippingSelectors } from '../../features/shipping';
import './PickupPointsMap.css';

const PickupPointsMap = () => {
    const dispatch = useDispatch();
    const points = useSelector(shippingSelectors.selectPickupPoints);
    const loading = useSelector(shippingSelectors.selectPickupPointsLoading);
    const error = useSelector(shippingSelectors.selectPickupPointsError);
    const selectedPoint = useSelector(shippingSelectors.selectSelectedPickupPoint);
    const selectedOption = useSelector(shippingSelectors.selectSelectedOption);
    const address = useSelector(shippingSelectors.selectAddress);

    const shippingMethod = selectedOption ? selectedOption.split('::')[0] : 'cdek';

    const [modalOpen, setModalOpen] = useState(false);
    const [activePoint, setActivePoint] = useState(null);
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (!modalOpen) return;
        const handleEsc = (e) => {
            if (e.key === 'Escape') setModalOpen(false);
        };
        document.addEventListener('keydown', handleEsc);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', handleEsc);
            document.body.style.overflow = '';
        };
    }, [modalOpen]);

    const mapContainerRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const placemarkMapRef = useRef({});
    const listRef = useRef(null);

    useEffect(() => {
        if (address?.city) {
            dispatch(fetchPickupPoints(shippingMethod));
        }
    }, [dispatch, shippingMethod, address?.city]);

    const filteredPoints = useMemo(() => {
        if (!search.trim()) return points;
        const q = search.toLowerCase();
        return points.filter(p =>
            (p.address || '').toLowerCase().includes(q) ||
            (p.company || '').toLowerCase().includes(q) ||
            (p.objectTypeName || '').toLowerCase().includes(q)
        );
    }, [points, search]);

    useEffect(() => {
        if (!modalOpen || !points.length) return;

        const timer = setTimeout(() => {
            if (!mapContainerRef.current) return;
            if (mapInstanceRef.current) {
                mapInstanceRef.current.destroy();
                mapInstanceRef.current = null;
            }

            const initMap = () => {
                const map = new window.ymaps.Map(mapContainerRef.current, {
                    center: [55.75, 37.62],
                    zoom: 10,
                    controls: ['zoomControl', 'geolocationControl'],
                });

                const clusterer = new window.ymaps.Clusterer({
                    preset: 'islands#invertedRedClusterIcons',
                    clusterHideIconOnBalloonOpen: false,
                    geoObjectHideIconOnBalloonOpen: false,
                });

                const pmMap = {};

                const placemarks = points.map((point) => {
                    const pm = new window.ymaps.Placemark(
                        point.geo,
                        {
                            balloonContentHeader: `<strong>${point.objectTypeName || ''} ${point.company || ''}</strong>`,
                            balloonContentBody: [
                                `<div style="font-size:13px;line-height:1.5;max-width:280px">`,
                                `<div>${point.address || ''}</div>`,
                                point.phone ? `<div>Тел: ${point.phone}</div>` : '',
                                point.amount ? `<div style="margin-top:4px;font-weight:600">Доставка: ${point.amount} руб.</div>` : '',
                                `</div>`,
                            ].join(''),
                            balloonContentFooter: `<button onclick="window.__selectPickupPoint__('${point.id}')" style="
                                margin-top:8px;width:100%;padding:8px 16px;background:#D00202;color:#fff;border:none;border-radius:6px;
                                font-size:14px;font-weight:600;cursor:pointer">Выбрать</button>`,
                        },
                        { preset: 'islands#redDotIcon' }
                    );
                    pm.events.add('click', () => {
                        setActivePoint(point);
                    });
                    pmMap[point.id] = pm;
                    return pm;
                });

                placemarkMapRef.current = pmMap;
                clusterer.add(placemarks);
                map.geoObjects.add(clusterer);

                if (placemarks.length > 0) {
                    const bounds = clusterer.getBounds();
                    if (bounds) {
                        if (bounds[0][0] === bounds[1][0]) { bounds[0][0] -= 2; bounds[1][0] += 2; }
                        if (bounds[0][1] === bounds[1][1]) { bounds[0][1] -= 2; bounds[1][1] += 2; }
                        map.setBounds(bounds, { checkZoomRange: true, zoomMargin: 40 });
                    }
                }

                mapInstanceRef.current = map;
            };

            if (window.ymaps) {
                window.ymaps.ready(initMap);
            }
        }, 100);

        return () => {
            clearTimeout(timer);
            if (mapInstanceRef.current) {
                mapInstanceRef.current.destroy();
                mapInstanceRef.current = null;
            }
        };
    }, [modalOpen, points]);

    useEffect(() => {
        window.__selectPickupPoint__ = (pointId) => {
            const point = points.find(p => String(p.id) === String(pointId));
            if (point) {
                dispatch(selectPickupPoint(point));
                setModalOpen(false);
                setActivePoint(null);
                setSearch('');
            }
        };
        return () => { delete window.__selectPickupPoint__; };
    }, [points, dispatch]);

    const handleListItemClick = (point) => {
        setActivePoint(point);
        const map = mapInstanceRef.current;
        const pm = placemarkMapRef.current[point.id];
        if (map && pm) {
            map.setCenter(point.geo, 15, { duration: 300 });
            pm.balloon.open();
        }
    };

    const handleConfirm = () => {
        if (activePoint) {
            dispatch(selectPickupPoint(activePoint));
            setModalOpen(false);
            setActivePoint(null);
            setSearch('');
        }
    };

    const handleClearPoint = () => {
        dispatch(selectPickupPoint(null));
        setActivePoint(null);
    };

    const openModal = () => {
        setModalOpen(true);
        setActivePoint(null);
        setSearch('');
    };

    if (loading) {
        return (
            <div className="pickup-map-loading">
                <Spin indicator={<LoadingOutlined spin />} />
                <span>Загрузка пунктов выдачи...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="pickup-map-error">
                Ошибка загрузки пунктов: {error}
            </div>
        );
    }

    if (points.length === 0) return null;

    return (
        <div className="pickup-section">
            {selectedPoint ? (
                <div className="pickup-point-selected">
                    <div className="pickup-point-selected-info">
                        <EnvironmentOutlined className="pickup-point-selected-icon" />
                        <div className="pickup-point-selected-details">
                            <div className="pickup-point-selected-title">
                                {selectedPoint.objectTypeName} {selectedPoint.company}
                            </div>
                            <div className="pickup-point-selected-address">
                                {selectedPoint.address}
                            </div>
                            {selectedPoint.amount > 0 && (
                                <div className="pickup-point-selected-cost">
                                    Доставка: {selectedPoint.amount} руб.
                                </div>
                            )}
                        </div>
                        <button className="pickup-point-change-btn" onClick={openModal}>
                            Изменить
                        </button>
                    </div>
                </div>
            ) : (
                <Button
                    type="default"
                    icon={<EnvironmentOutlined />}
                    onClick={openModal}
                    className="pickup-open-map-btn"
                    block
                >
                    Выбрать пункт выдачи на карте ({points.length} {getPointsWord(points.length)})
                </Button>
            )}

            {modalOpen && createPortal(
                <div className="pickup-modal-overlay" onClick={() => setModalOpen(false)}>
                    <div className="pickup-modal" onClick={e => e.stopPropagation()}>
                        <div className="pickup-modal-header">
                            <h3>Пункты выдачи</h3>
                            <button className="pickup-modal-close" onClick={() => setModalOpen(false)}>
                                <CloseOutlined />
                            </button>
                        </div>

                        <div className="pickup-modal-body">
                            <div className="pickup-modal-sidebar">
                                <Input
                                    placeholder="Поиск по адресу..."
                                    prefix={<SearchOutlined />}
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    className="pickup-search"
                                    allowClear
                                />
                                <div className="pickup-list" ref={listRef}>
                                    {filteredPoints.map(point => (
                                        <div
                                            key={point.id}
                                            className={`pickup-list-item ${activePoint?.id === point.id ? 'active' : ''}`}
                                            onClick={() => handleListItemClick(point)}
                                        >
                                            <div className="pickup-list-item-name">
                                                {point.objectTypeName} {point.company}
                                            </div>
                                            <div className="pickup-list-item-address">
                                                {point.address}
                                            </div>
                                            {point.amount > 0 && (
                                                <div className="pickup-list-item-cost">
                                                    {point.amount} руб.
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    {filteredPoints.length === 0 && (
                                        <div className="pickup-list-empty">Ничего не найдено</div>
                                    )}
                                </div>
                            </div>

                            <div className="pickup-modal-map-area">
                                <div
                                    className="pickup-modal-map"
                                    ref={mapContainerRef}
                                    onClick={e => e.stopPropagation()}
                                    onMouseDown={e => e.stopPropagation()}
                                />

                                {activePoint && (
                                    <div className="pickup-modal-footer">
                                        <div className="pickup-modal-footer-info">
                                            <AimOutlined />
                                            <div>
                                                <span className="pickup-modal-footer-name">
                                                    {activePoint.objectTypeName} {activePoint.company}
                                                </span>
                                                <span className="pickup-modal-footer-address">
                                                    {activePoint.address}
                                                </span>
                                            </div>
                                        </div>
                                        <Button
                                            type="primary"
                                            onClick={handleConfirm}
                                            className="pickup-modal-confirm-btn"
                                        >
                                            Забрать отсюда
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

function getPointsWord(n) {
    const abs = Math.abs(n) % 100;
    const lastDigit = abs % 10;
    if (abs > 10 && abs < 20) return 'пунктов';
    if (lastDigit > 1 && lastDigit < 5) return 'пункта';
    if (lastDigit === 1) return 'пункт';
    return 'пунктов';
}

export default PickupPointsMap;
